import type { CatalogUsageDto } from "../../../api-client";

/**
 * The delete gate for the Heilmittel, Preise and Diagnosegruppen tabs.
 *
 * These three used to offer a bare `window.confirm` and let the server sort it
 * out — which it did not: the therapy delete removed the row's price history
 * before it discovered it was blocked, and the diagnosis-group delete was not
 * blocked at all. The server refuses both properly now, so this exists to make
 * the refusal legible rather than a red toast: ask what references the row,
 * and if anything does, say what and do not offer the delete at all.
 *
 * The server's `blockers` are English (they are the API-level message, shared
 * with the catalog endpoints); `counts` is the structured form, so the German
 * wording lives here next to the rest of the screen's German.
 */

const COUNT_LABELS: Record<string, (n: number) => string> = {
  slots: (n) => `${n} geplante oder erbrachte Termine`,
  continuousSlots: (n) => `${n} Dauertermine`,
  cancelledSlots: (n) => `${n} abgesagte Termine`,
  therapists: (n) => `${n} Therapeut:innen mit dieser Qualifikation`,
  rxs: (n) => `${n} Verordnungen`,
};

/** Counts that describe rows deleted WITH the record, not references to it. */
const OWNED_COUNTS = new Set(["prices"]);

const describe = (usage: CatalogUsageDto): string[] =>
  Object.entries(usage.counts ?? {})
    .filter(([key, n]) => n > 0 && !OWNED_COUNTS.has(key))
    .map(([key, n]) => COUNT_LABELS[key]?.(n) ?? `${n} × ${key}`);

/**
 * Returns true only when the admin confirmed a delete that is actually
 * allowed. A blocked row never reaches a confirm prompt — there is nothing to
 * confirm, and offering one trains people to click through it.
 */
export const confirmDeleteWithUsage = (label: string, usage: CatalogUsageDto): boolean => {
  if (!usage.deletable) {
    const reasons = describe(usage);
    window.alert(
      `"${label}" kann nicht gelöscht werden.\n\n` +
        `Noch verknüpft mit:\n${reasons.map((r) => `  • ${r}`).join("\n")}\n\n` +
        `Diese Verweise müssen erhalten bleiben, damit Abrechnung und ` +
        `Behandlungshistorie gültig bleiben. Nicht mehr benötigte Einträge ` +
        `können stattdessen auf "nicht in Verwendung" gesetzt werden.`
    );
    return false;
  }

  const owned = Number(usage.counts?.prices ?? 0);
  const alsoDeleted = owned
    ? `\n\nDabei werden ${owned} zugehörige Preiszeile(n) mit gelöscht.`
    : "";
  return window.confirm(`"${label}" wirklich löschen?${alsoDeleted}`);
};
